import { ColDef, ICellRendererParams } from 'ag-grid-community';
import styles from './Enach.module.scss';
import { Button, Typography } from '@navi/web-ui/lib/primitives';
import Download from '@cp/src/assets/images/icons/Download';
import { useMemo, useState } from 'react';
import { CancelIcon, CheckIcon } from '@navi/web-ui/lib/icons';
import { Tooltip, TooltipContent, TooltipTrigger } from '@cp/src/components/TooltipV2/TooltipV2';
import cx from 'classnames';
import { FILE_STATUS } from './Constants/types';
import dayjs from 'dayjs';
import { DateTimeFormat } from '@cp/src/components/DateTimePicker/constants';
import axiosInstance, { ApiKeys, getApiUrl } from '@cp/src/utils/ApiHelper';
import { downloadDocumentFromS3WithoughtPreview } from '@cp/src/utils/DocumentDownloadFromS3';
import { toast } from '@navi/web-ui/lib/primitives/Toast';
import CircularLoader from './Constants/CircularLoaderIconCopy';
export const useColumnDefs = (): {
  columnDefs: ColDef[];
  handleFileButtonClick: (referenceId: string, fileName: string) => void;
} => {
  const [isDownloading, setIsDownloading] = useState(false);
  const handleFileButtonClick = (referenceId: string, fileName: string) => {
    setIsDownloading(true);
    const url = getApiUrl(ApiKeys.DOWNLOAD_ENACH_FILE, { referenceId });
    axiosInstance
      .get(url)
      .then(res => {
        if (res?.data?.preSignedGetUrl) {
          downloadDocumentFromS3WithoughtPreview(
            res?.data?.preSignedGetUrl,
            fileName,
            `Cannot Download the File`
          );
        }
      })
      .catch(() => {
        toast(`Error in downloading the File`, { type: 'error' });
      })
      .finally(() => setIsDownloading(false));
  };

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        field: 'fileName',
        headerName: 'File name',
        wrapHeaderText: true,
        wrapText: true,
        width: 240,
        cellStyle: { cursor: 'auto' },
        cellRenderer: (params: ICellRendererParams) => {
          const { data } = params;
          return (
            <div className="flex gap-1 justify-between">
              <Tooltip placement="bottom" hiddenPadding={0} hideStrategy="referenceHidden">
                <TooltipTrigger>
                  <div className={styles.fileName}>{data?.fileName}</div>
                </TooltipTrigger>
                <TooltipContent
                  className={cx(styles.tooltipWrapper, styles.uploadHistoryTooltipContent)}
                >
                  {data?.fileName}
                </TooltipContent>
              </Tooltip>
              {
                <Button
                  variant="text"
                  disabled={isDownloading}
                  className={styles.buttonWrapper}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleFileButtonClick(data?.referenceId, data?.fileName)}
                >
                  <Download />
                </Button>
              }
            </div>
          );
        }
      },
      {
        field: 'totalLans',
        headerName: 'Total LANs',
        wrapHeaderText: true,
        wrapText: true,
        width: 120,
        cellStyle: { cursor: 'auto' },
        cellRenderer: (params: ICellRendererParams) => {
          const { data } = params;
          return data?.status === FILE_STATUS.COMPLETED ? (
            <>
              <div>
                <Typography variant="p4" color="var(--grayscale-2)">
                  {data?.totalLans}
                </Typography>
              </div>
            </>
          ) : (
            <Typography variant="p4" color="var(--grayscale-2)">
              -
            </Typography>
          );
        }
      },
      {
        field: 'createdAtEpochMillis',
        headerName: 'Upload Date & Time',
        wrapHeaderText: false,
        cellStyle: { cursor: 'auto' },
        cellRenderer: (params: ICellRendererParams) => {
          const { data } = params;
          return (
            <span className={styles.uploadTimeText}>
              {dayjs(data?.createdAtEpochMillis).local().format(DateTimeFormat.DATE_TIME)}
            </span>
          );
        }
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 160,
        cellRenderer: (params: ICellRendererParams) => {
          if (params?.value === FILE_STATUS.COMPLETED) {
            return (
              <div className={styles.successStatusContainer}>
                <CheckIcon color="var(--green-base)" />
                <Typography className={styles.successText} variant="p3">
                  Success
                </Typography>
              </div>
            );
          } else if (params?.value === FILE_STATUS.PENDING) {
            return (
              <div className={styles.pendingStatusContainer}>
                <CircularLoader fill="var(--pending-fill-color)" />
                <Typography className={styles.pendingText} variant="p3">
                  In Progress
                </Typography>
              </div>
            );
          } else {
            return (
              <div className={styles.failedStatusContainer}>
                <CancelIcon color="var(--red-base)" />
                <Typography className={styles.failureText} variant="p3">
                  Failed
                </Typography>
              </div>
            );
          }
        }
      }
    ],
    []
  );
  return { columnDefs, handleFileButtonClick };
};

export default useColumnDefs;
